import { useState, useEffect, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useClientAuth } from '../../context/ClientAuthContext'
import { useClientSync } from '../../hooks/useClientSync'
import {
  LayoutDashboard, Bot, CreditCard, FileText,
  AlertTriangle, BarChart3, Sun, Moon, Monitor, Receipt, Mic
} from 'lucide-react'
import { useBranding, logoFor, onLogoError } from '../../branding'
// The whole shell (sidebar / top bar / rail / …) is the client's layout theme,
// branding.layout — a reseller's client gets the reseller's (set on the dashboard).
import PortalShell from '../layouts/PortalShell'

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/bots', icon: Bot, label: 'My Bots' },
  { path: '/stats', icon: BarChart3, label: 'Statistics' },
  { path: '/recordings', icon: Mic, label: 'Recordings' },
  { path: '/billing', icon: CreditCard, label: 'Billing' },
  { path: '/invoices', icon: Receipt, label: 'Invoices' },
  { path: '/requests', icon: FileText, label: 'Requests' }
]

export default function ClientLayout() {
  const { client, logout, refreshClient, token, setClient } = useClientAuth()
  const navigate = useNavigate()

  // The client's logo, colours, favicon and title (falls back to ours).
  const branding = client?.branding || null
  useBranding(branding)
  const brandName = branding?.title || client?.company || client?.name || 'Redstone AI'

  // Refresh client data (balance, etc.) periodically
  useEffect(() => {
    refreshClient()
    const interval = setInterval(refreshClient, 60000)
    return () => clearInterval(interval)
  }, [])

  // SSE Sync Handlers
  const handleBalanceUpdate = useCallback((data) => {
    setClient(prev => prev ? { ...prev, balance: data.balance_after } : prev)
  }, [setClient])

  const handleGracePeriodUpdate = useCallback((data) => {
    setClient(prev => prev ? {
      ...prev,
      grace_period_active: data.grace_period_active,
      admin_extension_days: data.extension_days
    } : prev)
  }, [setClient])

  // SSE Connection for real-time updates
  const { connected: sseConnected, reconnecting } = useClientSync({
    token,
    onBalanceUpdate: handleBalanceUpdate,
    onGracePeriodUpdate: handleGracePeriodUpdate,
    enabled: !!token
  })

  // Theme state: 'light', 'dark', or 'auto'. Until the user picks one, the
  // client's default theme (branding.theme) applies.
  const hasThemeChoice = () => localStorage.getItem('redstone-theme-auto') != null
  const [themeMode, setThemeMode] = useState(() => {
    if (!hasThemeChoice() && ['light', 'dark'].includes(branding?.theme)) return branding.theme
    const isAuto = localStorage.getItem('redstone-theme-auto') !== 'false'
    if (isAuto) return 'auto'
    return localStorage.getItem('redstone-theme') || 'dark'
  })
  const [themeChosen, setThemeChosen] = useState(hasThemeChoice)
  useEffect(() => {
    if (!themeChosen && ['light', 'dark', 'auto'].includes(branding?.theme)) setThemeMode(branding.theme)
  }, [branding?.theme, themeChosen])

  // Track actual applied theme (for logo selection)
  const [appliedTheme, setAppliedTheme] = useState('dark')

  useEffect(() => {
    const updateAppliedTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme') || 'dark'
      setAppliedTheme(theme)
    }
    updateAppliedTheme()
    // Watch for theme changes
    const observer = new MutationObserver(updateAppliedTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  // Update theme when themeMode changes (the branding default is applied
  // without saving it as the user's own choice)
  useEffect(() => {
    if (!themeChosen) {
      if (themeMode !== 'auto') document.documentElement.setAttribute('data-theme', themeMode)
      return
    }
    if (window.themeUtils) {
      if (themeMode === 'auto') {
        window.themeUtils.setAutoTheme()
      } else {
        window.themeUtils.setTheme(themeMode)
      }
    }
  }, [themeMode, themeChosen])

  const cycleTheme = () => {
    setThemeChosen(true)
    // Cycle: auto -> light -> dark -> auto
    if (themeMode === 'auto') setThemeMode('light')
    else if (themeMode === 'light') setThemeMode('dark')
    else setThemeMode('auto')
  }

  const getThemeIcon = () => {
    if (themeMode === 'auto') return Monitor
    if (themeMode === 'light') return Sun
    return Moon
  }

  const getThemeLabel = () => {
    if (themeMode === 'auto') return 'Auto'
    if (themeMode === 'light') return 'Light'
    return 'Dark'
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const formatBalance = (balance) => {
    const num = parseFloat(balance) || 0
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  const showGraceBanner = client?.balance <= 0 || !!client?.grace_period_active

  return (
    <PortalShell
      layout={branding?.layout}
      navItems={navItems}
      brandName={brandName}
      logoSrc={logoFor(branding, appliedTheme)}
      onLogoError={(e) => onLogoError(e, appliedTheme)}
      user={{
        name: client?.name || 'Client',
        initials: getInitials(client?.name),
        sub: formatBalance(client?.balance),
        subClass: parseFloat(client?.balance) <= 0 ? 'text-danger' : 'text-success'
      }}
      theme={{ Icon: getThemeIcon(), label: getThemeLabel(), cycle: cycleTheme }}
      sync={sseConnected ? 'connected' : reconnecting ? 'reconnecting' : 'disconnected'}
      onLogout={handleLogout}
    >
      {showGraceBanner && (
        <GracePeriodBanner
          balance={client?.balance}
          graceActive={client?.grace_period_active}
          graceStarted={client?.grace_period_started}
          extensionDays={client?.admin_extension_days}
        />
      )}
      <Outlet />
    </PortalShell>
  )
}

function GracePeriodBanner({ balance, graceActive, graceStarted, extensionDays }) {
  const { clientApi } = useClientAuth()
  const isUrgent = graceActive

  // Calculate days remaining in grace period
  const getDaysRemaining = () => {
    if (!graceActive || !graceStarted) return null
    const graceDuration = (extensionDays || 0) + 7 // Default 7 days + any admin extension
    const startDate = new Date(graceStarted)
    const endDate = new Date(startDate.getTime() + graceDuration * 24 * 60 * 60 * 1000)
    const now = new Date()
    const daysLeft = Math.ceil((endDate - now) / (24 * 60 * 60 * 1000))
    return Math.max(0, daysLeft)
  }

  const daysRemaining = getDaysRemaining()

  const handleActivateGrace = async () => {
    try {
      await clientApi('/grace-period', { method: 'POST' })
      window.location.reload()
    } catch (error) {
      console.error('Failed to activate grace period:', error)
    }
  }

  return (
    <div className={`grace-banner ${isUrgent ? 'urgent' : ''}`} style={{
      padding: 'var(--space-md) var(--space-lg)',
      background: isUrgent
        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(239, 68, 68, 0.08) 100%)',
      border: `1px solid ${isUrgent ? 'rgba(239, 68, 68, 0.3)' : 'rgba(245, 158, 11, 0.25)'}`,
      borderRadius: 'var(--radius-lg)',
      marginBottom: 'var(--space-lg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'var(--space-md)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: isUrgent ? 'rgba(220, 38, 38, 0.15)' : 'rgba(245, 158, 11, 0.15)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isUrgent ? 'var(--danger)' : 'var(--warning)',
          flexShrink: 0
        }}>
          <AlertTriangle size={24} />
        </div>
        <div>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isUrgent ? 'var(--danger)' : 'var(--warning)',
            marginBottom: '2px'
          }}>
            {graceActive
              ? `Grace Period Active${daysRemaining !== null ? ` - ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining` : ''}`
              : 'Low Balance Warning'}
          </h3>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0 }}>
            {graceActive
              ? 'Your bots will be suspended when the grace period expires. Please add funds to continue.'
              : `Your account balance is $${parseFloat(balance).toFixed(2)}. Add funds to avoid service interruption.`
            }
          </p>
        </div>
      </div>

      {!graceActive && parseFloat(balance) <= 0 && (
        <button
          onClick={handleActivateGrace}
          style={{
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--warning)',
            color: '#0f172a',
            fontSize: '0.875rem',
            fontWeight: 600,
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Activate Grace Period
        </button>
      )}
    </div>
  )
}
